import React, { useEffect, useState, useRef } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useStytchUser } from '@stytch/react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCurrentUser } from '../store/user/slice';
import { AppDispatch, RootState } from '../store';
import { Text } from '../components/Typography/Typography';
import styles from './ProtectedRoutes.module.scss';
import { getSessionToken } from '../api/api';
import { socketService } from '../socket/socketService';
import { setGlobalAlert } from '../store/app/slice';
import { AlertType } from '../store/app/types';
import { identifyUser } from '../services/tracking';

interface ProtectedRoutesProps {
  requiredRole?: 'user' | 'admin';
}

export const ProtectedRoutes: React.FC<ProtectedRoutesProps> = ({ requiredRole }) => {
  const { user: stytchUser } = useStytchUser();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { currentUser, error } = useSelector((state: RootState) => state.user);
  const [isVerifying, setIsVerifying] = useState(true);
  const lastFetchRef = useRef<number>(0);
  const DEBOUNCE_INTERVAL = 2000; // 2 seconds

  useEffect(() => {
    const verifyAuth = async () => {
      if (!stytchUser) {
        setIsVerifying(false);
        return;
      }

      const now = Date.now();
      if (now - lastFetchRef.current < DEBOUNCE_INTERVAL) {
        return;
      }

      try {
        lastFetchRef.current = now;
        await dispatch(fetchCurrentUser()).unwrap();

        const sessionToken = getSessionToken();
        if (sessionToken && stytchUser) {
          const wsConnected = await socketService.initialize(stytchUser.user_id);
          if (!wsConnected) {
            console.warn('Failed to establish WebSocket connection');
            // Show a warning in the global alert banner
            dispatch(
              setGlobalAlert({
                type: AlertType.WARNING,
                message:
                  'Unable to establish real-time connection. Chat messages may be delayed. Please refresh the page to try again.',
              }),
            );
          } else {
            console.log('WebSocket connection established');
          }
        }
      } catch (err) {
        navigate('/unauthorized');
      } finally {
        setIsVerifying(false);
      }
    };

    verifyAuth();

    // Clean up WebSocket connection when component unmounts
    return () => {
      socketService.disconnect();
    };
  }, [stytchUser]);

  // Identify the user in analytics when available
  useEffect(() => {
    if (currentUser?.id) {
      identifyUser(currentUser.id);
    }
  }, [currentUser?.id]);

  if (isVerifying) {
    return (
      <div className={styles.loadingContainer}>
        <Text size="large" weight="medium">
          Loading...
        </Text>
      </div>
    );
  }

  // If user is not authenticated with Stytch, redirect to login
  if (!stytchUser) {
    return <Navigate to="/login" replace />;
  }

  // If there's an error fetching user data, show unauthorized page
  if (error) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check if user has required role
  if (requiredRole && currentUser?.role !== requiredRole) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Render child routes if user is authenticated and has required role
  return <Outlet />;
};
